import { Button } from '@superdispatch/ui-lab';
import { isWebView } from 'shared/constants/AppTypeConstants';
import { useCustomerSupport } from 'shared/helpers/CustomerSupport';
import { VerifiedCarrierApplicationStatusDTO } from 'shared/modules/carrier-profile/data/VerifiedCarrierApplicationStatusDTO';
import { useVerifiedCarrierApplicationStatus } from '../data/VerifiedCarrierApplicationStatusAPI';
import { FieldAdditionalMessages } from './AdditionalMessages';
import { Inline, Stack } from '@superdispatch/ui';
import { VerificationStatusMessages } from 'shared/modules/carrier-profile/core/VerificationStatusMessages';
import { Sync } from '@material-ui/icons';
import { Typography } from '@material-ui/core';
import { WebViewLink } from 'shared/routing/WebViewLinks';
import { useFmcsaSync } from 'shared/modules/carrier-profile/core/FmcsaSyncUtils';
import { useEffect, useRef } from 'react';
import { trackCarrierProfileEvent } from 'shared/modules/carrier-profile/data/CarrierProfileAnalytics';

interface TermsAndConditionsRevokeReasonProps {
  application: VerifiedCarrierApplicationStatusDTO['verified_carrier_application'];
}

function TermsAndConditionsRevokeReason({
  application,
}: TermsAndConditionsRevokeReasonProps) {
  const { showLauncherMessanger } = useCustomerSupport();
  const revokingReasons = application.revoking_reasons?.terms_and_conditions;
  const externalNotes = application.external_notes?.terms_and_conditions;

  if (!revokingReasons?.length) {
    return null;
  }

  return (
    <FieldAdditionalMessages
      variant="error"
      listStyle="plain"
      title="Your Verified Carrier Status was revoked. You can no longer submit requests on Super Loadboard."
      messages={revokingReasons}
      externalNotes={externalNotes}
      actions={
        <Button
          size="small"
          fullWidth={isWebView}
          variant="neutral"
          onClick={() => {
            showLauncherMessanger();
          }}
        >
          Contact Support
        </Button>
      }
    />
  );
}

interface AuthorityRevokeReasonProps {
  application: VerifiedCarrierApplicationStatusDTO['verified_carrier_application'];
  onSyncClicked: () => void;
}

function AuthorityRevokeReason({
  application,
  onSyncClicked,
}: AuthorityRevokeReasonProps) {
  const revokingReasons = application.reasons.operation;
  const externalNotes = application.external_notes?.usdot_certificate;

  const { fmcsaSync, isTimeElapsed, isPending } = useFmcsaSync();

  if (!revokingReasons.length) {
    return null;
  }

  return (
    <VerificationStatusMessages
      variant="error"
      title="We were Unable to Verify Your Authority on FMCSA"
      messages={revokingReasons}
      additionalMessage={
        <Stack space="none">
          <Typography color="textPrimary">
            Once FMCSA restores your authority, we’ll resubmit it automatically.
            If you experience a delay, click &quot;Sync to FMCSA&quot; to check
            immediately.
          </Typography>
          <WebViewLink
            target="_blank"
            rel="noreferrer"
            href="https://support.superdispatch.com/hc/en-us/articles/40225296315539-How-to-Reactivate-an-Inactive-USDOT-or-MC-Number"
          >
            Learn more
          </WebViewLink>
        </Stack>
      }
      externalNotes={externalNotes}
      actions={
        <Inline verticalAlign="center">
          <Button
            startIcon={<Sync />}
            size="small"
            pending={isPending}
            disabled={!isTimeElapsed}
            fullWidth={isWebView}
            variant="neutral"
            onClick={() => {
              onSyncClicked();
              fmcsaSync();
            }}
          >
            Sync to FMCSA
          </Button>
          {!isTimeElapsed && (
            <Typography color="textSecondary">
              Recently synced. Wait 5m before syncing again.
            </Typography>
          )}
        </Inline>
      }
    />
  );
}

export function CarrierOverviewRevokingReasons() {
  const lastStatus = useRef<string>();
  const { data } = useVerifiedCarrierApplicationStatus();
  const application = data?.verified_carrier_application;
  const isDeclined = application?.status === 'revoked';
  const isTermsAndConditions = application?.reasons.terms_and_conditions.length;
  const isAuthority = application?.reasons.operation.length;

  useEffect(() => {
    if (application && lastStatus.current) {
      trackCarrierProfileEvent({
        name: 'Carrier Synced with FMCSA',
        status_changed: application.status !== lastStatus.current,
        previous_status: lastStatus.current,
        new_status: application.status,
        has_authority_reasons: !!application.reasons.operation.length,
      });
      lastStatus.current = undefined;
    }
  }, [application, lastStatus]);

  if (isDeclined && isTermsAndConditions) {
    return <TermsAndConditionsRevokeReason application={application} />;
  }

  if (isDeclined && isAuthority) {
    return (
      <AuthorityRevokeReason
        application={application}
        onSyncClicked={() => {
          lastStatus.current = application.status;
        }}
      />
    );
  }

  return null;
}
